import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useNotify from '@/hooks/useNotify';
import { mockOrganizations, Organization } from '@/data/MockOrganizations';
import OrganizationsHeader from './components/OrganizationsHeader';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';
import { RequestParams, TOrganizationItem, TUserSelfInvitationItem } from '@/data/types';
import { useSelector } from 'react-redux';
import { getAllOrganizations, getCurrentSessionUser, getUserInvitations } from '@/data/store/selectors';
import {
  acceptSelfInvitationAsync,
  getAllOrganizationsAsync,
  getSelfInvitationsAsync,
  rejectSelfInvitationAsync,
  storeOrganizationId,
} from '@/data/store/actions';
import { useAppDispatch } from '@/data/store/utils/redux';
import { motion } from 'framer-motion';
import OrganizationCard from './components/OrganizationCard';
import { Building2, Plus } from 'lucide-react';

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [processingInvitations, setProcessingInvitations] = useState<Set<string>>(new Set());
  const [invitationsKey, setInvitationsKey] = useState(0);
  const [organizationsKey, setOrganizationsKey] = useState(0);

  // Redux selectors
  const organizationsData = useSelector(getAllOrganizations);
  const invitationsData = useSelector(getUserInvitations);
  const currentUser = useSelector(getCurrentSessionUser);

  // API action wrappers
  const attemptGetAllOrganizations = useCallback(
    async (params: RequestParams) => {
      // Add search query to params if exists
      const searchParams = searchQuery.trim() ? { ...params, search: searchQuery.trim() } : params;
      return await dispatch(getAllOrganizationsAsync(searchParams));
    },
    [dispatch, searchQuery],
  );

  const attemptGetSelfInvitations = useCallback(
    async (params: RequestParams) => {
      return await dispatch(getSelfInvitationsAsync(params));
    },
    [dispatch],
  );

  // Helper functions for default images
  const getDefaultImage = useCallback((index: number): string => {
    const imageNumber = (index % 11) + 1;
    return `/images/img${imageNumber}.jpg`;
  }, []);

  const getDefaultImageByOrgId = useCallback((orgId: string): string => {
    let hash = 0;
    for (let i = 0; i < orgId.length; i++) {
      const char = orgId.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    const imageNumber = (Math.abs(hash) % 11) + 1;
    return `/images/img${imageNumber}.jpg`;
  }, []);

  // Map API organization to existing card format
  const mapOrganizationToCardProps = useCallback((org: TOrganizationItem) => {
    return {
      id: org.id,
      name: org.name,
      description: org.description || 'Δεν υπάρχει περιγραφή',
      logo: org.name.charAt(0).toUpperCase(), // Use first letter as logo
      location: org.location_id ? 'Ελλάδα' : 'Άγνωστη τοποθεσία',
      industry: getOrganizationType(org),
      memberCount: org.members || 0,
      energySystems: Math.floor(Math.random() * 50) + 1, // Mock data for now
      totalEnergyGenerated: `${Math.floor(Math.random() * 1000) + 100} kWh`,
      subscription: getSubscriptionType(org),
      status: getOrganizationStatus(org),
      lastActive: getLastActiveTime(org),
    };
  }, []);

  // Helper functions
  const getOrganizationType = (org: TOrganizationItem): string => {
    if (org.name.includes('Δήμος')) return 'Δημοτική Αρχή';
    if (org.name.includes('Περιφέρεια')) return 'Περιφερειακή Αρχή';
    if (org.name.includes('Υπουργείο')) return 'Κυβερνητικός Οργανισμός';
    if (org.name.includes('ΚΕΠ') || org.name.includes('Κέντρο')) return 'Υπηρεσία Πολιτών';
    return 'Οργανισμός';
  };

  const getSubscriptionType = (org: TOrganizationItem): 'enterprise' | 'pro' | 'basic' => {
    if (org.members && org.members > 100) return 'enterprise';
    if (org.members && org.members > 20) return 'pro';
    return 'basic';
  };

  const getOrganizationStatus = (org: TOrganizationItem): 'active' | 'pending' | 'inactive' => {
    return 'active'; // Default to active, adjust based on your API
  };

  const getLastActiveTime = (org: TOrganizationItem): string => {
    return 'Σήμερα'; // Mock data, replace with actual logic
  };

  // Event handlers
  const handleSelectOrganization = useCallback(
    (orgId: string) => {
      dispatch(storeOrganizationId(orgId));
    },
    [dispatch, navigate, organizationsData],
  );

  const handleCreateNewOrganization = useCallback(() => {
    navigate('/organizations/create');
  }, [navigate]);

  const handleAcceptInvitation = useCallback(
    async (invitationId: string) => {
      setProcessingInvitations((prev) => new Set(prev).add(invitationId));
      try {
        await dispatch(acceptSelfInvitationAsync(invitationId));
        setInvitationsKey((prev) => prev + 1);
        setOrganizationsKey((prev) => prev + 1);
        notify.success('Η πρόσκληση έγινε αποδεκτή!');
      } catch (error) {
        console.error('Failed to accept invitation:', error);
        notify.error('Σφάλμα κατά την αποδοχή της πρόσκλησης');
      } finally {
        setProcessingInvitations((prev) => {
          const newSet = new Set(prev);
          newSet.delete(invitationId);
          return newSet;
        });
      }
    },
    [dispatch],
  );

  const handleRejectInvitation = useCallback(
    async (invitationId: string) => {
      setProcessingInvitations((prev) => new Set(prev).add(invitationId));
      try {
        await dispatch(rejectSelfInvitationAsync(invitationId));
        setInvitationsKey((prev) => prev + 1);
        setOrganizationsKey((prev) => prev + 1);
        notify.success('Η πρόσκληση απορρίφθηκε');
      } catch (error) {
        console.error('Failed to reject invitation:', error);
        notify.error('Σφάλμα κατά την απόρριψη της πρόσκλησης');
      } finally {
        setProcessingInvitations((prev) => {
          const newSet = new Set(prev);
          newSet.delete(invitationId);
          return newSet;
        });
      }
    },
    [dispatch],
  );

  // Invitation Card Component
  const InvitationCard = ({ invitation, index }: { invitation: TUserSelfInvitationItem; index: number }) => {
    const isProcessing = processingInvitations.has(invitation.id);
    const defaultImage = getDefaultImage(index);

    return (
      <motion.div
        className="group relative cursor-pointer overflow-hidden rounded-3xl bg-gradient-to-br from-blue-50 to-purple-50 p-6 shadow-sm transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.08 }}
        whileHover={{ y: -6 }}
      >
        {/* Invitation Badge */}
        <div className="absolute top-4 right-4 z-10">
          <div className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">Πρόσκληση</div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

        {/* Content */}
        <div className="relative z-20">
          {/* Header */}
          <div className="mb-4 flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-xl font-bold text-white shadow-sm">
              {invitation.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{invitation.name}</h3>
              {invitation.contact_email && <p className="text-sm text-gray-500">{invitation.contact_email}</p>}
            </div>
          </div>

          {/* Description */}
          {invitation.description && (
            <p className="mb-6 line-clamp-2 text-sm text-gray-600">{invitation.description}</p>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <motion.button
              onClick={() => handleAcceptInvitation(invitation.id)}
              disabled={isProcessing}
              className="flex flex-1 items-center justify-center rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:bg-green-400"
              whileTap={{ scale: 0.95 }}
            >
              {isProcessing ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                'Αποδοχή'
              )}
            </motion.button>
            <motion.button
              onClick={() => handleRejectInvitation(invitation.id)}
              disabled={isProcessing}
              className="flex flex-1 items-center justify-center rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:bg-red-400"
              whileTap={{ scale: 0.95 }}
            >
              {isProcessing ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                'Απόρριψη'
              )}
            </motion.button>
          </div>

          {/* Website Link */}
          {invitation.website_url && (
            <a
              href={invitation.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 block text-center text-xs text-blue-600 transition-colors hover:text-blue-700"
            >
              Επισκεφθείτε την ιστοσελίδα →
            </a>
          )}
        </div>
      </motion.div>
    );
  };

  // Render function for Invitations
  const renderInvitations = useCallback(
    (invitations: TUserSelfInvitationItem[], state: any) => {
      if (invitations.length === 0 && !state.loading) {
        return null; // Don't show empty state for invitations
      }

      return (
        <div className="px-8 pb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <h2 className="mb-2 text-2xl font-bold text-gray-900">Προσκλήσεις Οργανισμών / Έργων</h2>
            <p className="text-gray-600">Έχετε προσκλήσεις από τους παρακάτω οργανισμούς / έργα</p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {invitations.map((invitation, index) => (
              <InvitationCard key={invitation.id} invitation={invitation} index={index} />
            ))}
          </div>
        </div>
      );
    },
    [processingInvitations],
  );

  // Render function for Organizations
  const renderOrganizations = useCallback(
    (organizations: TOrganizationItem[], state: any) => {
      return (
        <div className="px-8 pb-16">
          <motion.div
            className="grid grid-cols-1 gap-8 lg:grid-cols-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, staggerChildren: 0.08 }}
          >
            {/* Organization Cards */}
            {organizations.map((org, index) => {
              return (
                <motion.div
                  key={org.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <OrganizationCard organization={org} onSelect={() => handleSelectOrganization(org.id)} />
                </motion.div>
              );
            })}

            {/* Create New Organization Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: organizations.length * 0.08 }}
              className="group cursor-pointer"
              onClick={handleCreateNewOrganization}
            >
              <div className="relative h-full min-h-[320px] rounded-3xl border-2 border-dashed border-gray-200 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 hover:shadow-2xl hover:shadow-gray-100">
                <div className="flex h-full flex-col items-center justify-center text-center">
                  {/* Modern Icon Container */}
                  <div className="relative mb-8">
                    <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-gray-50 to-gray-100 shadow-sm transition-all duration-500 group-hover:scale-105 group-hover:shadow-lg">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-md transition-all duration-500 group-hover:shadow-xl">
                        <Plus className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    {/* Decorative rings */}
                    <div className="absolute inset-0 rounded-3xl border border-gray-100 opacity-0 transition-all duration-500 group-hover:scale-110 group-hover:opacity-100" />
                    <div className="absolute inset-0 rounded-3xl border border-gray-50 opacity-0 transition-all duration-700 group-hover:scale-125 group-hover:opacity-100" />
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-gray-700">
                      Δημιουργία Νέου Οργανισμού / Έργου
                    </h3>
                    <p className="mx-auto max-w-sm text-gray-600 transition-colors duration-300 group-hover:text-gray-500">
                      Ξεκινήστε τη διαχείριση ενέργειας με έναν νέο οργανισμό ή έργο και αναπτύξτε το δίκτυό σας
                    </p>
                  </div>

                  {/* Action indicator */}
                  <div className="mt-8 flex items-center gap-2 text-sm text-gray-400 transition-colors duration-300 group-hover:text-blue-500">
                    <span>Κάντε κλικ για να ξεκινήσετε</span>
                    <div className="h-1 w-4 rounded-full bg-current opacity-50 transition-all duration-500 group-hover:w-8 group-hover:opacity-100" />
                  </div>
                </div>

                {/* Background pattern */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-50/0 via-transparent to-gray-50/0 opacity-0 transition-opacity duration-500 group-hover:from-blue-50/30 group-hover:to-gray-50/30 group-hover:opacity-100" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      );
    },
    [mapOrganizationToCardProps, handleSelectOrganization, handleCreateNewOrganization],
  );

  // Loading and Error components
  const renderLoading = useCallback((isInitial: boolean, type: 'invitations' | 'organizations' = 'organizations') => {
    if (isInitial) {
      const message = type === 'invitations' ? 'Φόρτωση προσκλήσεων...' : 'Φόρτωση οργανισμών / έργων...';
      return (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
            <p className="text-gray-600">{message}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center py-8">
        <div className="mr-3 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        <p className="text-gray-600">Φόρτωση...</p>
      </div>
    );
  }, []);

  const renderError = useCallback((error: string, retry: () => void) => {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
          <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">Σφάλμα φόρτωσης</h3>
        <p className="mb-4 max-w-md text-center text-gray-500">{error}</p>
        <button
          onClick={retry}
          className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          Δοκιμάστε ξανά
        </button>
      </div>
    );
  }, []);

  const renderEmpty = useCallback(() => {
    return (
      <motion.div className="group cursor-pointer" onClick={handleCreateNewOrganization}>
        <div className="relative h-full min-h-[320px] rounded-3xl border-2 border-dashed border-gray-200 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 hover:shadow-2xl hover:shadow-gray-100">
          <div className="flex h-full flex-col items-center justify-center text-center">
            {/* Modern Icon Container */}
            <div className="relative mb-8">
              <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-gray-50 to-gray-100 shadow-sm transition-all duration-500 group-hover:scale-105 group-hover:shadow-lg">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-md transition-all duration-500 group-hover:shadow-xl">
                  <Plus className="h-6 w-6 text-white" />
                </div>
              </div>

              {/* Decorative rings */}
              <div className="absolute inset-0 rounded-3xl border border-gray-100 opacity-0 transition-all duration-500 group-hover:scale-110 group-hover:opacity-100" />
              <div className="absolute inset-0 rounded-3xl border border-gray-50 opacity-0 transition-all duration-700 group-hover:scale-125 group-hover:opacity-100" />
            </div>

            {/* Content */}
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-gray-700">
                Δημιουργία Νέου Οργανισμού / Έργου
              </h3>
              <p className="mx-auto max-w-sm text-gray-600 transition-colors duration-300 group-hover:text-gray-500">
                Ξεκινήστε τη διαχείριση ενέργειας με έναν νέο οργανισμό ή έργο και αναπτύξτε το δίκτυό σας
              </p>
            </div>

            {/* Action indicator */}
            <div className="mt-8 flex items-center gap-2 text-sm text-gray-400 transition-colors duration-300 group-hover:text-blue-500">
              <span>Κάντε κλικ για να ξεκινήσετε</span>
              <div className="h-1 w-4 rounded-full bg-current opacity-50 transition-all duration-500 group-hover:w-8 group-hover:opacity-100" />
            </div>
          </div>

          {/* Background pattern */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-50/0 via-transparent to-gray-50/0 opacity-0 transition-opacity duration-500 group-hover:from-blue-50/30 group-hover:to-gray-50/30 group-hover:opacity-100" />
        </div>
      </motion.div>
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl">
        <OrganizationsHeader searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        {/* Invitations Section */}
        <InfiniteScroll
          key={`invitations-${invitationsKey}`}
          apiAction={attemptGetSelfInvitations}
          itemsData={invitationsData}
          initialPageSize={20}
          debug={false}
          renderLoading={(isInitial) => renderLoading(isInitial, 'invitations')}
          renderError={renderError}
          renderEmpty={() => null}
          scroll={{
            threshold: 300,
            debounceMs: 200,
          }}
          loading={{
            showSpinner: true,
            text: 'Φόρτωση προσκλήσεων...',
          }}
          className="pb-4"
        >
          {renderInvitations}
        </InfiniteScroll>

        {/* Organizations with Infinite Scroll */}
        <InfiniteScroll
          key={`organizations-${organizationsKey}-${searchQuery}`}
          apiAction={attemptGetAllOrganizations}
          itemsData={organizationsData}
          initialPageSize={20}
          debug={false}
          renderLoading={(isInitial) => renderLoading(isInitial, 'organizations')}
          renderError={renderError}
          renderEmpty={renderEmpty}
          scroll={{
            threshold: 300,
            debounceMs: 200,
          }}
          loading={{
            showSpinner: true,
            text: 'Φόρτωση οργανισμών / έργων...',
          }}
          className="pb-8"
        >
          {renderOrganizations}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default Organizations;
